import { SortDescriptor } from "@nextui-org/react";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";

export type SortDirection = "ascending" | "descending" | null;

export function useSort<T>(data: T[], defaultSortModel?: SortDescriptor) {
  const [sortModel, setSortModel] = useState<SortDescriptor>(
    defaultSortModel ?? { column: "", direction: "ascending" }
  );

  const sortedData = useMemo(() => {
    if (!sortModel.column || !sortModel.direction) return data;

    return [...data].sort((a, b) => {
      const aValue = a[sortModel.column as keyof T];
      const bValue = b[sortModel.column as keyof T];

      // Handle strings that are valid dates
      const aDate = dayjs(aValue as string);
      const bDate = dayjs(bValue as string);

      const isADate = aDate.isValid();
      const isBDate = bDate.isValid();

      if (isADate && isBDate) {
        return sortModel.direction === "ascending"
          ? aDate.valueOf() - bDate.valueOf()
          : bDate.valueOf() - aDate.valueOf();
      }

      // Fallback: string comparison
      if (typeof aValue === "string" && typeof bValue === "string") {
        return sortModel.direction === "ascending"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      // Fallback: number comparison
      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortModel.direction === "ascending"
          ? aValue - bValue
          : bValue - aValue;
      }

      return 0;
    });
  }, [data, sortModel]);

  const handleSortModelChange = useCallback((model: SortDescriptor) => {
    setSortModel(model);
  }, []);

  return { sortedData, sortModel, setSortModel: handleSortModelChange };
}
